/* Native storefront navigation feedback. Never intercept or delay a link. */
(() => {
  'use strict';
  if (window.URANavigationFeedbackReady || !document.body) return;
  window.URANavigationFeedbackReady = true;

  const SHOW_AFTER = 300;
  const STALE_AFTER = 8000;
  const navigation = window.navigation;
  const observesNavigation = !!navigation && typeof navigation.addEventListener === 'function';
  const excludedControl = '[role="button"],[aria-haspopup="dialog"],[data-app-action],[data-dnav-bag],[data-dnav-account-open],[data-ura-size-open],[data-ura4-add],[data-ura-navigation-skip]';
  const host = document.createElement('div');
  host.id = 'UraNavigationFeedback';
  const line = document.createElement('span');
  line.className = 'ura-navigation-feedback__line';
  line.setAttribute('aria-hidden', 'true');
  const status = document.createElement('span');
  status.className = 'ura-navigation-feedback__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  host.append(line, status);
  document.body.append(host);
  let pending = null;

  function clear() {
    if (pending) {
      clearTimeout(pending.showTimer);
      clearTimeout(pending.staleTimer);
      pending.signal?.removeEventListener('abort', pending.onAbort);
      pending = null;
    }
    host.removeAttribute('data-active');
    status.textContent = '';
  }

  function destination(raw) {
    let url;
    try { url = new URL(raw, location.href); } catch (_) { return null; }
    if (!/^https?:$/.test(url.protocol) || url.origin !== location.origin || url.username || url.password || url.hash) return null;
    if (url.pathname === location.pathname && url.search === location.search) return null;
    // Only ordinary read-only storefront documents; never auth, carts, apps or checkout.
    const path = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
    if (!/^\/(?:$|(?:collections|products|pages|blogs|policies)(?:\/|$)|search\/?$)/.test(path)) return null;
    if (/\.(?:json|js|xml|pdf|zip|csv)(?:\/|$)/i.test(path)) return null;
    return url;
  }

  function eligibleLink(event) {
    if (event.defaultPrevented || event.isTrusted === false || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    const element = event.target?.closest ? event.target : event.target?.parentElement;
    const link = element?.closest('a[href]');
    if (!link || element.closest('button,input,select,textarea,[contenteditable="true"]') || link.closest(excludedControl) || link.hasAttribute('download')) return null;
    const target = (link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return null;
    if ((link.getAttribute('rel') || '').split(/\s+/).includes('external')) return null;
    const url = destination(link.href);
    return url ? { link, url } : null;
  }

  window.addEventListener('click', event => {
    const eligible = eligibleLink(event);
    clear();
    if (!eligible) return;
    const attempt = { href: eligible.url.href, event, navigationEvent: null, signal: null, onAbort: null, showTimer: 0, staleTimer: 0 };
    pending = attempt;
    attempt.showTimer = setTimeout(() => {
      if (pending !== attempt) return;
      // Read cancellation after every click/navigation handler has had its turn.
      if (event.defaultPrevented || attempt.navigationEvent?.defaultPrevented || attempt.signal?.aborted || document.visibilityState === 'hidden' || (observesNavigation && !attempt.navigationEvent)) {
        clear();
        return;
      }
      host.setAttribute('data-active', '');
      const language = (document.documentElement.lang || 'en').split('-')[0];
      status.textContent = language === 'ar' ? 'جارٍ فتح الصفحة' : language === 'es' ? 'Abriendo página' : 'Opening page';
    }, SHOW_AFTER);
    // Legacy browsers cannot report every native Stop/cancellation event. Never strand a cue.
    attempt.staleTimer = setTimeout(() => { if (pending === attempt) clear(); }, STALE_AFTER);
  }, { passive: true });

  if (observesNavigation) {
    navigation.addEventListener('navigate', event => {
      const attempt = pending;
      const url = destination(event.destination?.url);
      if (!attempt || !url || event.destination.sameDocument || event.hashChange || event.navigationType === 'traverse' || event.formData || event.downloadRequest != null || url.href !== attempt.href) {
        clear();
        return;
      }
      attempt.navigationEvent = event;
      attempt.signal = event.signal;
      attempt.onAbort = () => { if (pending === attempt) clear(); };
      if (event.signal?.aborted || event.defaultPrevented) clear();
      else event.signal?.addEventListener('abort', attempt.onAbort, { once: true });
    });
    navigation.addEventListener('navigateerror', clear);
    navigation.addEventListener('navigatesuccess', clear);
  }

  window.addEventListener('pagehide', clear);
  window.addEventListener('pageswap', clear);
  window.addEventListener('pageshow', clear);
  window.addEventListener('popstate', clear);
  window.addEventListener('hashchange', clear);
  window.addEventListener('offline', clear);
  window.addEventListener('pointerdown', clear, { passive: true });
  window.addEventListener('keydown', event => { if (event.key === 'Escape') clear(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') clear(); });
  document.addEventListener('ura:navigation-open', clear);
})();
